import { CodeInput } from "@/components/code-input/CodeInput.tsx";
import { EsignDeclineModal } from "@/pages/esign/widgets/EsignDeclineModal.tsx";
import { esignKeys, useEsignOtpRequest, useEsignOtpVerify } from "@/queries/use-esign.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiErrorException, ApiValidationError } from "@/types/api.types.ts";
import {
  Box,
  Checkbox,
  FontAwesomeIcon,
  Modal,
  ModalProps,
  Stack,
  Text,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

const CODE_VERIFY_DELAY_MS = 300;

export interface EsignOtpModalProps extends ModalProps {
  requestId: string;
  onVerified: () => void;
}

export function EsignOtpModal({ requestId, onVerified, ...props }: EsignOtpModalProps) {
  const { t } = useTranslation();
  const { close, openModal } = useModals();
  const { showInfo } = useToasts();
  const { id } = props;
  const queryClient = useQueryClient();

  const { mutate: requestOtp, data: otpRequest } = useEsignOtpRequest();
  const { mutate: verifyOtp, isPending: isVerifying } = useEsignOtpVerify();

  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(30);
  const [isChecking, setIsChecking] = useState(false);
  const [isConsentAccepted, setIsConsentAccepted] = useState(false);
  const initedRef = useRef(false);
  const verifyTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (initedRef.current) return;
    initedRef.current = true;
    requestOtp();
  }, [requestOtp]);

  useEffect(() => {
    return () => {
      if (verifyTimerRef.current) window.clearTimeout(verifyTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const handleResend = () => {
    if (resendCooldown > 0) return;
    requestOtp();
    setResendCooldown(30);
    setError(null);
    setCode("");
  };

  const handleVerify = (value?: string, fromPaste = false) => {
    const trimmed = (value ?? code).trim();
    if (trimmed.length !== 6 || isVerifying || isChecking || !otpRequest || !isConsentAccepted)
      return;
    setError(null);
    setIsChecking(true);
    verifyTimerRef.current = window.setTimeout(
      () => {
        verifyOtp(
          { code: trimmed, consent_version: otpRequest.consent_version },
          {
            onSuccess: () => {
              analyticsService.trackEvent(analyticEvents.esign.identityVerified, {
                [analyticProperties.id]: requestId,
              });
              void queryClient.invalidateQueries({
                queryKey: esignKeys.request(requestId).queryKey,
              });
              close(id);
              showInfo({
                title: t("core:esign_otp_verified"),
                icon: <FontAwesomeIcon icon={"fa-light fa-check"} />,
              });
              onVerified();
            },
            onError: (err) => {
              const apiError = (err as ApiErrorException).error as ApiValidationError | undefined;
              setError(apiError?.validationErrors?.[0]?.message || t("core:esign_otp_invalid"));
              setResendCooldown(0);
              setIsChecking(false);
            },
          }
        );
      },
      fromPaste ? 0 : CODE_VERIFY_DELAY_MS
    );
  };

  const handleDecline = () => {
    close(id);
    openModal(EsignDeclineModal, { requestId, withdrawConsentByDefault: true });
  };

  return (
    <Modal
      {...props}
      title={t("core:esign_email_verification")}
      variant={"middle"}
      size={"md"}
      withCloser={true}
    >
      <Stack gap={"lg"}>
        <Box>
          <Text size="lg">{`${t("core:esign_otp_sent")} `}</Text>
          <Text size="lg" className="font-semibold">
            {otpRequest?.email ?? t("core:esign_otp_sent_fallback")}
          </Text>
        </Box>
        {otpRequest && (
          <Stack gap={"sm"}>
            <Box className={"max-h-60 overflow-y-auto rounded-lg border border-dark/10 p-4"}>
              <Text className={"whitespace-pre-line"}>{otpRequest.consent_text}</Text>
            </Box>
            <Checkbox
              checked={isConsentAccepted}
              onChange={(e) => setIsConsentAccepted(e.target.checked)}
              title={t("core:esign_consent_agree")}
            />
            <Text
              size={"md"}
              onClick={handleDecline}
              className={"underline cursor-pointer text-secondary"}
            >
              {t("core:esign_consent_disagree")}
            </Text>
          </Stack>
        )}
        <Stack gap={"xs"}>
          <CodeInput
            label={t("core:esign_otp_code_label")}
            value={code}
            autoFocus={true}
            disabled={isChecking || !isConsentAccepted}
            onChange={(next) => {
              setCode(next);
              setError(null);
            }}
            onComplete={(next, fromPaste) => handleVerify(next, fromPaste)}
          />
          <Stack direction={"horizontal"} items={"center"} gap={"xs"} className="mt-2">
            {resendCooldown > 0 && !error && (
              <Text className={"text-secondary"} size={"md"}>
                {t("core:esign_otp_resend_in", { seconds: resendCooldown })}
              </Text>
            )}
            {resendCooldown === 0 && !error && (
              <Text
                size={"md"}
                onClick={handleResend}
                className={twMerge("underline cursor-pointer text-secondary")}
              >
                {t("core:esign_otp_resend")}
              </Text>
            )}
            {error && (
              <Box>
                <Text size={"md"} className="text-alert">
                  {`${t("core:esign_otp_invalid")} `}
                </Text>
                <Text size="md" className="text-secondary">
                  {`${t("core:esign_otp_try_again")} `}
                </Text>
                <Text
                  size="md"
                  onClick={resendCooldown > 0 ? undefined : handleResend}
                  className={twMerge(
                    "underline text-secondary",
                    resendCooldown > 0 ? "cursor-default" : "cursor-pointer"
                  )}
                >
                  {`${t("core:esign_otp_resend_inline")}.`}
                </Text>
              </Box>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
}
